import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText,
  Save,
  Loader2,
  Sparkles,
  XCircle,
  CheckCircle2,
  GitBranch,
  Link2,
  AlertTriangle,
  Rocket,
  Ban,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import {
  getArtifact,
  getArtifactFiles,
  saveArtifactFiles,
  rewriteArtifact,
  transitionArtifact,
  bumpArtifactVersion,
  rollbackArtifact,
  getHitlCheckpoints,
  previewHitlDiff as previewHitlDiffRequest,
  listArtifactDeps,
  getArtifactImpact,
  addArtifactDep,
  removeArtifactDep,
  type ArtifactDetail,
  type SkippedFile,
  type LintResult,
  type DepEntry,
  type ImpactReport,
  type HitlCheckpointEntry,
} from '../api/artifacts';
import CodeEditor from '../components/CodeEditor';
import DiffView from '../components/DiffView';
import HitlDiffPreview, { type HitlDiffPayload } from '../components/HitlDiffPreview';
import { useToast } from '../components/ui/toastContext';

const KIND_LABELS: Record<string, string> = {
  skill: '技能（Skill）',
  mcp: 'MCP 工具',
  kb: '知识库（KB）',
  preset: '能力预设',
};

const STATE_LABELS: Record<string, string> = {
  draft: '草稿',
  published: '已发布',
  deprecated: '已弃用',
};

const STATE_BADGE: Record<string, string> = {
  draft: 'secondary',
  published: 'success',
  deprecated: 'warning',
};

const formatTime = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('zh-CN');
};

// 提取后端错误文案：优先 response.data.detail（字符串），其次 Error.message
const apiErrorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return typeof err.message === 'string' ? err.message : '';
};

export default function ArtifactEditor() {
  const { artifactId } = useParams<{ artifactId: string }>();
  const toast = useToast();

  const [detail, setDetail] = useState<ArtifactDetail | null>(null);
  const [files, setFiles] = useState<Record<string, string>>({});
  const [skipped, setSkipped] = useState<SkippedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // 已修改但未保存的草稿（路径 -> 内容）
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [activePath, setActivePath] = useState('');
  const [changelog, setChangelog] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveLint, setSaveLint] = useState<LintResult | null>(null);
  const [savedVersion, setSavedVersion] = useState('');

  // 模型改写
  const [instruction, setInstruction] = useState('');
  const [rewriting, setRewriting] = useState(false);
  const [rewriteDiff, setRewriteDiff] = useState<Record<string, string> | null>(null);
  const [rewriteWarnings, setRewriteWarnings] = useState<string[]>([]);
  const [applying, setApplying] = useState(false);

  // 生命周期状态操作（transition）
  const [transitioning, setTransitioning] = useState(false);
  const [stateActionError, setStateActionError] = useState('');

  // 发布新版本（SemVer bump）
  const [bumpOpen, setBumpOpen] = useState(false);
  const [bumpPart, setBumpPart] = useState<'major' | 'minor' | 'patch'>('patch');
  const [bumpChangelog, setBumpChangelog] = useState('');
  const [bumping, setBumping] = useState(false);

  // 回滚确认（target = 目标版本号）
  const [rollbackTarget, setRollbackTarget] = useState<string | null>(null);
  const [rollbackChangelog, setRollbackChangelog] = useState('');
  const [rollingBack, setRollingBack] = useState(false);

  // 依赖关系与影响分析
  const [depsDown, setDepsDown] = useState<DepEntry[]>([]);
  const [depsUp, setDepsUp] = useState<DepEntry[]>([]);
  const [impact, setImpact] = useState<ImpactReport | null>(null);
  const [newDepId, setNewDepId] = useState('');
  const [depError, setDepError] = useState('');
  const [addingDep, setAddingDep] = useState(false);

  // HITL 人工确认契约（P4：变更预览）
  const [hitlCheckpoints, setHitlCheckpoints] = useState<HitlCheckpointEntry[]>([]);
  const [hitlPreview, setHitlPreview] = useState<Record<string, HitlDiffPayload>>({});
  const [hitlPreviewLoading, setHitlPreviewLoading] = useState('');

  // HITL 契约加载失败不影响编辑器主流程（按未标记处理）
  const loadHitl = useCallback(async () => {
    if (!artifactId) return;
    try {
      const checkpoints = await getHitlCheckpoints(artifactId);
      setHitlCheckpoints(checkpoints);
    } catch {
      setHitlCheckpoints([]);
    }
  }, [artifactId]);

  const loadDeps = useCallback(async () => {
    if (!artifactId) return;
    try {
      const [down, up, impactReport] = await Promise.all([
        listArtifactDeps(artifactId, 'down'),
        listArtifactDeps(artifactId, 'up'),
        getArtifactImpact(artifactId),
      ]);
      setDepsDown(down);
      setDepsUp(up);
      setImpact(impactReport);
    } catch {
      // 依赖/影响数据加载失败不影响编辑器主流程
      setDepsDown([]);
      setDepsUp([]);
      setImpact(null);
    }
  }, [artifactId]);

  const load = useCallback(async () => {
    if (!artifactId) return;
    setLoading(true);
    setLoadError('');
    try {
      const [detail, filesPayload] = await Promise.all([
        getArtifact(artifactId),
        getArtifactFiles(artifactId),
      ]);
      setDetail(detail);
      setFiles(filesPayload.files);
      setSkipped(filesPayload.skipped);
      setDrafts({});
      setActivePath((prev) =>
        prev && prev in filesPayload.files ? prev : Object.keys(filesPayload.files).sort()[0] || ''
      );
      await loadDeps();
      await loadHitl();
    } catch (e) {
      setLoadError(apiErrorMessage(e) || '加载产物失败');
    } finally {
      setLoading(false);
    }
  }, [artifactId, loadDeps, loadHitl]);

  // 初始加载：内联 promise 链，setState 均在异步回调中执行
  useEffect(() => {
    if (!artifactId) return;
    Promise.resolve()
      .then(() => {
        setLoading(true);
        setLoadError('');
        return Promise.all([getArtifact(artifactId), getArtifactFiles(artifactId)]);
      })
      .then(async ([detail, filesPayload]) => {
        setDetail(detail);
        setFiles(filesPayload.files);
        setSkipped(filesPayload.skipped);
        setDrafts({});
        setActivePath((prev) =>
          prev && prev in filesPayload.files ? prev : Object.keys(filesPayload.files).sort()[0] || ''
        );
        await loadDeps();
        await loadHitl();
      })
      .catch((e) => setLoadError(apiErrorMessage(e) || '加载产物失败'))
      .finally(() => setLoading(false));
  }, [artifactId, loadDeps, loadHitl]);

  const dirty = Object.keys(drafts).length > 0;
  const activeContent = activePath ? (drafts[activePath] ?? files[activePath] ?? '') : '';

  const save = async () => {
    if (!artifactId || saving || !dirty) return;
    setSaving(true);
    setSaveLint(null);
    setSavedVersion('');
    try {
      const merged = { ...files, ...drafts };
      const res = await saveArtifactFiles(artifactId, merged, changelog);
      setFiles(merged);
      setDrafts({});
      setChangelog('');
      setSaveLint(res.lint);
      setSavedVersion(res.version);
      // 刷新详情（版本号 + 版本历史）
      const detail = await getArtifact(artifactId);
      setDetail(detail);
      toast.success(`已保存，新版本 ${res.version}`);
    } catch (e) {
      toast.error('保存失败: ' + apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const generateRewrite = async () => {
    if (!artifactId || rewriting || !instruction.trim()) return;
    setRewriting(true);
    setRewriteDiff(null);
    setRewriteWarnings([]);
    try {
      const res = await rewriteArtifact(artifactId, instruction.trim(), false);
      const diff = (res.diff || {}) as Record<string, string>;
      setRewriteDiff(diff);
      setRewriteWarnings(res.warnings || []);
      if (Object.keys(diff).length === 0) {
        toast.info('模型没有产出任何修改');
      }
    } catch (e) {
      toast.error('生成改写失败: ' + apiErrorMessage(e));
    } finally {
      setRewriting(false);
    }
  };

  const applyRewrite = async () => {
    if (!artifactId || applying) return;
    setApplying(true);
    try {
      const res = await rewriteArtifact(artifactId, instruction.trim(), true);
      toast.success(`改写已应用，新版本 ${res.version}`);
      setRewriteDiff(null);
      setRewriteWarnings([]);
      setInstruction('');
      await load();
    } catch (e) {
      toast.error('应用改写失败: ' + apiErrorMessage(e));
    } finally {
      setApplying(false);
    }
  };

  // 提取后端错误文案（409 时 detail 可能是字符串或带 message 的对象）
  const errorText = (e: unknown): string => {
    const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object' && typeof (d as { message?: unknown }).message === 'string') {
      return (d as { message: string }).message;
    }
    return typeof err.message === 'string' ? err.message : '操作失败';
  };

  // 生命周期状态流转：发布（draft→published）/ 弃用（published→deprecated）
  const transition = async (targetState: 'published' | 'deprecated') => {
    if (!artifactId || transitioning) return;
    setTransitioning(true);
    setStateActionError('');
    try {
      const updated = await transitionArtifact(artifactId, targetState);
      setDetail(updated);
      toast.success(targetState === 'published' ? '已发布' : '已弃用');
    } catch (e) {
      // 409（非法流转 / 级联阻断 / 乐观锁冲突）在操作区下方展示中文错误
      setStateActionError(errorText(e));
    } finally {
      setTransitioning(false);
    }
  };

  // 发布新版本（SemVer bump；major 破坏性变更强制 changelog）
  const bump = async () => {
    if (!artifactId || bumping) return;
    if (bumpPart === 'major' && !bumpChangelog.trim()) {
      toast.error('major（破坏性变更）必须填写变更说明');
      return;
    }
    setBumping(true);
    try {
      const res = await bumpArtifactVersion(artifactId, bumpPart, bumpChangelog);
      toast.success(`已发布新版本 ${res.version}`);
      setBumpOpen(false);
      setBumpChangelog('');
      await load();
    } catch (e) {
      toast.error('发布新版本失败: ' + errorText(e));
    } finally {
      setBumping(false);
    }
  };

  // 回滚到 published 历史版本（生成新版本快照，历史不改写）
  const rollback = async () => {
    if (!artifactId || !rollbackTarget || rollingBack) return;
    setRollingBack(true);
    try {
      const res = await rollbackArtifact(artifactId, rollbackTarget, rollbackChangelog);
      toast.success(`已回滚到 ${rollbackTarget}，新版本 ${res.version}`);
      setRollbackTarget(null);
      setRollbackChangelog('');
      await load();
    } catch (e) {
      toast.error('回滚失败: ' + errorText(e));
    } finally {
      setRollingBack(false);
    }
  };

  // 添加依赖（当前产物引用目标产物）；成环等 409 错误就地展示
  const addDep = async () => {
    if (!artifactId || addingDep || !newDepId.trim()) return;
    setAddingDep(true);
    setDepError('');
    try {
      await addArtifactDep(artifactId, newDepId.trim());
      setNewDepId('');
      toast.success('已添加依赖');
      await loadDeps();
    } catch (e) {
      setDepError(errorText(e));
    } finally {
      setAddingDep(false);
    }
  };

  // 删除下游依赖边
  const removeDep = async (toId: string) => {
    if (!artifactId) return;
    setDepError('');
    try {
      await removeArtifactDep(artifactId, toId);
      toast.success('已删除依赖');
      await loadDeps();
    } catch (e) {
      setDepError(errorText(e));
    }
  };

  // HITL 变更预览（P4）：契约未携带 diff 时，以后端 diff 服务生成示例变更的行级差异
  const handlePreviewHitlDiff = async (cp: HitlCheckpointEntry) => {
    if (!artifactId || hitlPreviewLoading) return;
    setHitlPreviewLoading(cp.id);
    try {
      const diff = await previewHitlDiffRequest(artifactId, {
        checkpoint_id: cp.id,
        label: `checkpoint/${cp.id}`,
        before: { prompt: cp.prompt, blocking: cp.blocking },
        after: { prompt: `${cp.prompt}（示例修改）`, blocking: cp.blocking },
      });
      setHitlPreview((prev) => ({ ...prev, [cp.id]: diff as HitlDiffPayload }));
    } catch (e) {
      toast.error('生成变更预览失败: ' + errorText(e));
    } finally {
      setHitlPreviewLoading('');
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <Loader2 size={20} className="animate-spin" style={{ marginRight: 8 }} /> 加载产物中...
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="container">
        <div className="card" style={{ borderColor: 'var(--color-error)' }}>
          <div className="flex items-center gap-2">
            <XCircle size={16} className="text-error" />
            <strong className="text-error">加载失败</strong>
          </div>
          <p className="text-muted text-sm mt-2">{loadError}</p>
        </div>
      </div>
    );
  }

  const sortedPaths = Object.keys(files).sort();

  return (
    <div className="container">
      <h1>
        <FileText size={22} className="inline mr-1" /> 产物编辑器
      </h1>
      {detail && (
        <div className="mb-4">
          <p className="text-muted" style={{ marginBottom: 8 }}>
            {detail.name}
            <span className="badge secondary" style={{ marginLeft: 8 }}>
              {KIND_LABELS[detail.kind] || detail.kind}
            </span>
            <span className="badge info" style={{ marginLeft: 8 }}>
              当前版本 {detail.version}
            </span>
            <span
              className={`badge ${STATE_BADGE[detail.state] || 'secondary'}`}
              style={{ marginLeft: 8 }}
            >
              {STATE_LABELS[detail.state] || detail.state}
            </span>
          </p>
          {/* 状态操作：draft → published（发布）/ published → deprecated（弃用） */}
          <div className="flex items-center gap-2">
            {detail.state === 'draft' && (
              <button
                className="primary"
                onClick={() => transition('published')}
                disabled={transitioning}
              >
                {transitioning ? (
                  <Loader2 size={14} className="inline animate-spin mr-1" />
                ) : (
                  <Rocket size={14} className="inline mr-1" />
                )}
                发布
              </button>
            )}
            {detail.state === 'published' && (
              <button
                className="secondary"
                onClick={() => transition('deprecated')}
                disabled={transitioning}
              >
                {transitioning ? (
                  <Loader2 size={14} className="inline animate-spin mr-1" />
                ) : (
                  <Ban size={14} className="inline mr-1" />
                )}
                弃用
              </button>
            )}
          </div>
          {stateActionError && (
            <div
              className="card mt-2"
              style={{ padding: 12, borderColor: 'var(--color-error)' }}
            >
              <div className="flex items-center gap-2">
                <XCircle size={16} className="text-error" />
                <strong className="text-error">状态变更失败</strong>
              </div>
              <p className="text-muted text-sm mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                {stateActionError}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-4" style={{ alignItems: 'flex-start' }}>
        {/* 左侧：文件列表 + 版本历史 */}
        <div style={{ width: 280, flexShrink: 0 }}>
          <div className="card" style={{ padding: 12 }}>
            <h3 style={{ marginBottom: 12 }}>文件列表（{sortedPaths.length}）</h3>
            <div className="flex flex-col" style={{ gap: 4 }}>
              {sortedPaths.map((path) => {
                const active = path === activePath;
                const modified = path in drafts;
                return (
                  <button
                    key={path}
                    onClick={() => setActivePath(path)}
                    style={{
                      textAlign: 'left',
                      padding: '6px 10px',
                      fontSize: 13,
                      borderRadius: 'var(--radius-md)',
                      border: `1px solid ${active ? 'var(--color-primary)' : 'transparent'}`,
                      background: active ? 'rgba(88, 166, 255, 0.1)' : 'transparent',
                      color: active ? 'var(--color-primary)' : 'var(--text-secondary)',
                      justifyContent: 'flex-start',
                      gap: 6,
                    }}
                  >
                    <FileText size={13} style={{ flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {path}
                    </span>
                    {modified && (
                      <span
                        title="有未保存的修改"
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: 'var(--color-warning)',
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
            {skipped.length > 0 && (
              <div className="text-muted text-xs mt-4">
                以下文件不支持在线编辑：
                <ul style={{ paddingLeft: 16, margin: '4px 0 0' }}>
                  {skipped.map((s) => (
                    <li key={s.path}>
                      {s.path}（{s.reason}）
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* 版本历史：state 徽标 + 回滚（仅 published 历史版本）+ 发布新版本（SemVer bump） */}
          <div className="card mt-4" style={{ padding: 12 }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>
                <GitBranch size={14} className="inline mr-1" /> 版本历史
              </h3>
              <button
                className="secondary"
                style={{ fontSize: 12, padding: '4px 8px' }}
                onClick={() => {
                  setBumpOpen((prev) => !prev);
                  setRollbackTarget(null);
                }}
              >
                发布新版本
              </button>
            </div>

            {bumpOpen && (
              <div
                className="mb-4"
                style={{
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-default)',
                }}
              >
                <select
                  aria-label="版本档位"
                  value={bumpPart}
                  onChange={(e) => setBumpPart(e.target.value as 'major' | 'minor' | 'patch')}
                  style={{ width: '100%', marginBottom: 8 }}
                >
                  <option value="patch">patch（修复）</option>
                  <option value="minor">minor（新功能）</option>
                  <option value="major">major（破坏性变更，必填变更说明）</option>
                </select>
                <input
                  type="text"
                  aria-label="新版本变更说明"
                  placeholder={bumpPart === 'major' ? '变更说明（major 必填）' : '变更说明（可选）'}
                  value={bumpChangelog}
                  onChange={(e) => setBumpChangelog(e.target.value)}
                  style={{ width: '100%', marginBottom: 8 }}
                />
                <div className="flex gap-2">
                  <button
                    className="primary"
                    style={{ fontSize: 12, padding: '4px 10px' }}
                    onClick={bump}
                    disabled={bumping}
                  >
                    {bumping ? <Loader2 size={12} className="inline animate-spin mr-1" /> : null}
                    确认发布
                  </button>
                  <button
                    className="secondary"
                    style={{ fontSize: 12, padding: '4px 10px' }}
                    onClick={() => setBumpOpen(false)}
                    disabled={bumping}
                  >
                    取消
                  </button>
                </div>
              </div>
            )}

            {detail && detail.versions.length > 0 ? (
              <div className="flex flex-col" style={{ gap: 8 }}>
                {detail.versions.map((v) => {
                  const current = v.version === detail.version;
                  const canRollback = v.state === 'published' && !current;
                  return (
                    <div
                      key={v.id}
                      style={{
                        padding: '8px 10px',
                        borderRadius: 'var(--radius-md)',
                        border: `1px solid ${current ? 'var(--color-primary)' : 'var(--border-default)'}`,
                        background: current ? 'rgba(88, 166, 255, 0.08)' : 'transparent',
                      }}
                    >
                      <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: 13 }}>{v.version}</strong>
                        <span className={`badge ${STATE_BADGE[v.state] || 'secondary'} text-xs`}>
                          {STATE_LABELS[v.state] || v.state}
                        </span>
                        {current && <span className="badge info text-xs">当前版本</span>}
                      </div>
                      {v.changelog && <div className="text-muted text-xs mt-1">{v.changelog}</div>}
                      <div className="text-muted text-xs mt-1">{formatTime(v.created_at)}</div>
                      {canRollback && rollbackTarget !== v.version && (
                        <button
                          className="secondary mt-2"
                          style={{ fontSize: 12, padding: '3px 8px' }}
                          onClick={() => {
                            setRollbackTarget(v.version);
                            setRollbackChangelog('');
                            setBumpOpen(false);
                          }}
                        >
                          回滚到此版本
                        </button>
                      )}
                      {rollbackTarget === v.version && (
                        <div className="mt-2">
                          <div className="text-muted text-xs" style={{ marginBottom: 6 }}>
                            确认回滚到 {v.version}？将生成新版本快照，历史版本不会被改写。
                          </div>
                          <input
                            type="text"
                            aria-label="回滚变更说明"
                            placeholder="回滚说明（可选）"
                            value={rollbackChangelog}
                            onChange={(e) => setRollbackChangelog(e.target.value)}
                            style={{ width: '100%', marginBottom: 6, fontSize: 12 }}
                          />
                          <div className="flex gap-2">
                            <button
                              className="primary"
                              style={{ fontSize: 12, padding: '3px 8px' }}
                              onClick={rollback}
                              disabled={rollingBack}
                            >
                              {rollingBack ? (
                                <Loader2 size={12} className="inline animate-spin mr-1" />
                              ) : null}
                              确认回滚
                            </button>
                            <button
                              className="secondary"
                              style={{ fontSize: 12, padding: '3px 8px' }}
                              onClick={() => setRollbackTarget(null)}
                              disabled={rollingBack}
                            >
                              取消
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-muted text-sm">暂无版本记录</div>
            )}
          </div>

          {/* 依赖关系：下游（我引用谁）/ 上游（谁引用我） */}
          <div className="card mt-4" style={{ padding: 12 }}>
            <h3 style={{ marginBottom: 12 }}>
              <Link2 size={14} className="inline mr-1" /> 依赖关系
            </h3>

            <div className="text-muted text-xs" style={{ marginBottom: 6 }}>
              下游（我引用谁，{depsDown.length}）
            </div>
            {depsDown.length > 0 ? (
              <div className="flex flex-col mb-4" style={{ gap: 6 }}>
                {depsDown.map((d) => (
                  <div
                    key={d.artifact_id}
                    className="flex items-center gap-2"
                    style={{ fontSize: 13, flexWrap: 'wrap' }}
                  >
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.name}
                    </span>
                    <span className="text-muted text-xs">
                      {KIND_LABELS[d.kind] || d.kind} v{d.version}
                    </span>
                    <span className={`badge ${STATE_BADGE[d.state] || 'secondary'} text-xs`}>
                      {STATE_LABELS[d.state] || d.state}
                    </span>
                    <button
                      className="icon-btn"
                      aria-label={`删除依赖 ${d.name}`}
                      title="删除依赖"
                      onClick={() => removeDep(d.artifact_id)}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted text-sm mb-4">暂无下游依赖</div>
            )}

            <div className="text-muted text-xs" style={{ marginBottom: 6 }}>
              上游（谁引用我，{depsUp.length}）
            </div>
            {depsUp.length > 0 ? (
              <div className="flex flex-col mb-4" style={{ gap: 6 }}>
                {depsUp.map((d) => (
                  <div
                    key={d.artifact_id}
                    className="flex items-center gap-2"
                    style={{ fontSize: 13, flexWrap: 'wrap' }}
                  >
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.name}
                    </span>
                    <span className="text-muted text-xs">
                      {KIND_LABELS[d.kind] || d.kind} v{d.version}
                    </span>
                    <span className={`badge ${STATE_BADGE[d.state] || 'secondary'} text-xs`}>
                      {STATE_LABELS[d.state] || d.state}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted text-sm mb-4">暂无上游依赖方</div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="text"
                aria-label="目标产物 ID"
                placeholder="目标产物 ID（UUID）"
                value={newDepId}
                onChange={(e) => setNewDepId(e.target.value)}
                style={{ flex: 1, fontSize: 12 }}
              />
              <button
                className="secondary"
                style={{ fontSize: 12, padding: '4px 8px' }}
                onClick={addDep}
                disabled={addingDep || !newDepId.trim()}
              >
                {addingDep ? <Loader2 size={12} className="inline animate-spin" /> : '添加依赖'}
              </button>
            </div>
            {depError && (
              <div className="text-error text-xs mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                {depError}
              </div>
            )}
          </div>

          {/* 影响分析：上游依赖方 stale / published 统计 */}
          <div className="card mt-4" style={{ padding: 12 }}>
            <h3 style={{ marginBottom: 12 }}>
              <AlertTriangle size={14} className="inline mr-1" /> 影响分析
            </h3>
            {impact ? (
              <>
                <div className="text-sm" style={{ marginBottom: 8 }}>
                  上游依赖方 {impact.dependent_count} 个，其中已发布{' '}
                  {impact.published_dependent_count} 个
                  {impact.stale_count > 0 && (
                    <span className="badge warning text-xs" style={{ marginLeft: 6 }}>
                      {impact.stale_count} 个版本滞后
                    </span>
                  )}
                </div>
                {impact.dependents.length > 0 && (
                  <div className="flex flex-col" style={{ gap: 6 }}>
                    {impact.dependents.map((d) => (
                      <div
                        key={d.artifact_id}
                        style={{
                          padding: '6px 8px',
                          borderRadius: 'var(--radius-md)',
                          border: `1px solid ${d.stale ? 'var(--color-warning)' : 'var(--border-default)'}`,
                          background: d.stale ? 'rgba(240, 136, 62, 0.08)' : 'transparent',
                          fontSize: 13,
                        }}
                      >
                        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                          <span>{d.name}</span>
                          <span className={`badge ${STATE_BADGE[d.state] || 'secondary'} text-xs`}>
                            {STATE_LABELS[d.state] || d.state}
                          </span>
                          {d.stale && (
                            <span className="badge warning text-xs">
                              <AlertTriangle size={10} className="inline mr-1" />
                              版本滞后（锁定 v{d.locked_member_version}）
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted text-sm">暂无依赖方</div>
            )}
          </div>

          {/* HITL 人工确认契约（P4）：展示契约并渲染变更 diff 预览 */}
          {hitlCheckpoints.length > 0 && (
            <div className="card mt-4" style={{ padding: 12 }}>
              <h3 style={{ marginBottom: 12 }}>
                <ShieldCheck size={14} className="inline mr-1" /> 人工确认契约（{hitlCheckpoints.length}）
              </h3>
              <div className="flex flex-col" style={{ gap: 8 }}>
                {hitlCheckpoints.map((cp) => (
                  <div
                    key={cp.id}
                    style={{
                      padding: '8px 10px',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border-default)',
                      fontSize: 13,
                    }}
                  >
                    <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                      <strong>{cp.name}</strong>
                      <code className="text-muted text-xs">{cp.id}</code>
                      <span className={`badge ${cp.blocking ? 'warning' : 'secondary'} text-xs`}>
                        {cp.blocking ? '阻断' : '仅提示'}
                      </span>
                    </div>
                    <div className="text-muted text-xs mt-1">{cp.prompt}</div>
                    {/* 确认什么：契约自带 diff 直接渲染；否则可生成示例变更预览 */}
                    {(cp.diff || hitlPreview[cp.id]) && (
                      <div className="mt-2">
                        <HitlDiffPreview diff={(cp.diff || hitlPreview[cp.id]) as HitlDiffPayload} />
                      </div>
                    )}
                    {!cp.diff && !hitlPreview[cp.id] && (
                      <button
                        className="secondary mt-2"
                        style={{ fontSize: 12, padding: '3px 8px' }}
                        onClick={() => handlePreviewHitlDiff(cp)}
                        disabled={hitlPreviewLoading === cp.id}
                      >
                        {hitlPreviewLoading === cp.id ? (
                          <Loader2 size={12} className="inline animate-spin" />
                        ) : (
                          '变更预览'
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 右侧：编辑区 + 保存 + 模型改写 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="card">
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>
                {activePath ? (
                  <>
                    编辑 <code>{activePath}</code>
                    {activePath in drafts && (
                      <span className="badge warning text-xs" style={{ marginLeft: 8 }}>
                        未保存
                      </span>
                    )}
                  </>
                ) : (
                  '暂无可编辑文件'
                )}
              </h3>
            </div>
            {activePath && (
              <CodeEditor
                ariaLabel={`编辑 ${activePath}`}
                value={activeContent}
                onChange={(value) => setDrafts((prev) => ({ ...prev, [activePath]: value }))}
              />
            )}

            <div className="flex items-center gap-2 mt-4">
              <input
                type="text"
                aria-label="变更说明"
                placeholder="变更说明（可选，写入版本快照）"
                value={changelog}
                onChange={(e) => setChangelog(e.target.value)}
                style={{ flex: 1 }}
              />
              <button className="primary" onClick={save} disabled={saving || !dirty}>
                {saving ? (
                  <>
                    <Loader2 size={14} className="inline animate-spin mr-1" /> 保存中...
                  </>
                ) : (
                  <>
                    <Save size={14} className="inline mr-1" /> 保存
                  </>
                )}
              </button>
            </div>

            {/* 保存后的 lint 结果 */}
            {saveLint && (
              <div className="mt-4">
                <div className="flex items-center gap-2">
                  {saveLint.passed ? (
                    <span className="badge success">
                      <CheckCircle2 size={12} className="inline mr-1" /> 质量检查通过
                    </span>
                  ) : (
                    <span className="badge error">
                      <XCircle size={12} className="inline mr-1" /> {saveLint.errors.length} 个错误
                    </span>
                  )}
                  {savedVersion && <span className="badge info">新版本 {savedVersion}</span>}
                </div>
                {saveLint.errors.length > 0 && (
                  <ul className="text-error text-sm mt-2" style={{ paddingLeft: 18 }}>
                    {saveLint.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                )}
                {saveLint.warnings.length > 0 && (
                  <ul className="text-muted text-sm mt-2" style={{ paddingLeft: 18 }}>
                    {saveLint.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* 模型改写 */}
          <div className="card mt-4">
            <h3>
              <Sparkles size={16} className="inline mr-1" /> 模型改写
            </h3>
            <p className="text-muted text-sm mt-2">
              用自然语言描述不满意点，由模型改写产物文件；确认 diff 后再应用。
            </p>
            <div className="flex items-center gap-2 mt-4">
              <input
                type="text"
                aria-label="改写指令"
                placeholder="例如：把步骤三的错误处理写得更详细"
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                style={{ flex: 1 }}
              />
              <button
                className="secondary"
                onClick={generateRewrite}
                disabled={rewriting || !instruction.trim()}
              >
                {rewriting ? (
                  <>
                    <Loader2 size={14} className="inline animate-spin mr-1" /> 生成中...
                  </>
                ) : (
                  <>
                    <Sparkles size={14} className="inline mr-1" /> 生成改写
                  </>
                )}
              </button>
            </div>

            {rewriteDiff && (
              <div className="mt-4">
                {Object.keys(rewriteDiff).length > 0 ? (
                  <>
                    <div className="text-sm text-muted mb-4">
                      以下 {Object.keys(rewriteDiff).length} 个文件将被修改：
                    </div>
                    <div className="flex flex-col gap-4">
                      {Object.entries(rewriteDiff).map(([path, diff]) => (
                        <div key={path}>
                          <code className="text-sm" style={{ display: 'block', marginBottom: 4 }}>
                            {path}
                          </code>
                          <DiffView diff={diff} />
                        </div>
                      ))}
                    </div>
                    {rewriteWarnings.length > 0 && (
                      <ul className="text-muted text-sm mt-4" style={{ paddingLeft: 18 }}>
                        {rewriteWarnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    )}
                    <div className="flex gap-2 mt-4">
                      <button className="primary" onClick={applyRewrite} disabled={applying}>
                        {applying ? (
                          <>
                            <Loader2 size={14} className="inline animate-spin mr-1" /> 应用中...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 size={14} className="inline mr-1" /> 确认应用
                          </>
                        )}
                      </button>
                      <button
                        className="secondary"
                        onClick={() => {
                          setRewriteDiff(null);
                          setRewriteWarnings([]);
                        }}
                        disabled={applying}
                      >
                        <X size={14} className="inline mr-1" /> 放弃
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-muted text-sm">模型没有产出任何修改。</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
